import type { IAccount } from "@/modules/account/common/types";
import type { IGroupTransferBeneficiary } from "@/modules/transaction/common/types";
import { addEditBeneficiaryValidationSchema } from "@/modules/transaction/validations";
import { FormContainer } from "@ab.uitools/ui-library/components/FormContainer";
import { Modal } from "@ab.uitools/ui-library/components/Modal";
import { Text } from "@ab.uitools/ui-library/components/Text";
import type { TFormData } from "@ab.uitools/ui-library/types/globalTypes";
import { type Dispatch, type SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AddEditBeneficiaryForm } from "./AddEditBeneficiaryForm";

interface Props {
  isOpen: boolean;
  isEdit?: boolean;
  onClose: () => void;
  rowIndex: number | null;
  beneficiary: IGroupTransferBeneficiary | null;
  accountsList: IAccount[];
  groupTransferBeneficiariesList: IGroupTransferBeneficiary[];
  setGroupTransferBeneficiariesList: Dispatch<SetStateAction<IGroupTransferBeneficiary[]>>;
}

interface ISubmitProps {
  isDirty: boolean;
  values: TFormData;
}

const initialValues = {
  account: "",
  beneficiary: "",
  amount: "",
  bankName: "",
};

export const AddEditBeneficiaryModal = ({
  isOpen,
  isEdit,
  onClose,
  rowIndex,
  beneficiary,
  accountsList,
  groupTransferBeneficiariesList,
  setGroupTransferBeneficiariesList,
}: Props) => {
  const { t } = useTranslation();
  const [isAccountNumberInvalid, setIsAccountNumberInvalid] = useState<boolean>(false);
  const hasSubmitted = useRef(false);

  const handleSubmit = ({ values, isDirty }: ISubmitProps) => {
    if (isDirty && !hasSubmitted.current) {
      hasSubmitted.current = true;
      const data = {
        receiverAccount: values.account,
        receiverName: values.beneficiary,
        amount: values.amount,
        bankName: values.bankName,
        beneficiaryLegalStatus: values.beneficiaryLegalStatus,
      };
      if (isEdit) {
        const newBeneficiariesList = groupTransferBeneficiariesList.map((beneficiary, i) => {
          if (rowIndex === i) {
            return data;
          }
          return beneficiary;
        });
        setGroupTransferBeneficiariesList(newBeneficiariesList);
      } else {
        setGroupTransferBeneficiariesList((prev) => ([
          ...prev,
          data,
        ]));
      }
      onClose();
    }
  };

  const ownAccounts = useMemo(() => {
    return accountsList.map((account) => account.number);
  }, [accountsList]);

  useEffect(() => {
    if (isOpen) {
      hasSubmitted.current = false;
    }
  }, [isOpen]);

  return (
    <Modal
      closeIcon
      closeOnOutsideClick={false}
      titleProps={{
        title: t("transfer.add.beneficiary.title"),
        size: "large"
      }}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={() => {}}
      className="add-beneficiary-modal"
      buttonProps={{
        confirm: {
          formId: "add-beneficiary-form",
          buttonText: t(isEdit ? "general.save" : "general.add"),
          buttonActionType: "submit",
          className: "add-beneficiary-modal--submit-button",
        },
        cancel: { buttonText: t("general.cancel") },
      }}
    >
      <Text type="secondary" className="mb-28">
        {t("transfer.add.beneficiary.subtitle")}
      </Text>
      <FormContainer
        mode="onBlur"
        reValidateMode="onBlur"
        className="full-width"
        formId="add-beneficiary-form"
        initialValues={initialValues}
        onSubmit={(values, formState) => handleSubmit({ values, isDirty: formState.isDirty })}
        validationScheme={addEditBeneficiaryValidationSchema({
          t,
          groupTransferBeneficiariesList,
          isEdit,
          isAccountNumberInvalid,
          selectedRowAccount: beneficiary?.receiverAccount as string,
          ownAccounts,
        })}
      >
        <AddEditBeneficiaryForm
          beneficiary={beneficiary}
          setIsAccountNumberInvalid={setIsAccountNumberInvalid}
          isEdit={isEdit}
        />
      </FormContainer>
    </Modal>
  );
};
